import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import api, { unwrap, errorMessage } from '../../api/client';
import * as payoutsApi from '../../api/payouts.api';

const BANK_OPTIONS = [
  { code: 'MBBEMYKL', name: 'Maybank' },
  { code: 'CIBBMYKL', name: 'CIMB Bank' },
  { code: 'PBBEMYKL', name: 'Public Bank' },
  { code: 'RHBBMYKL', name: 'RHB Bank' },
  { code: 'HLBBMYKL', name: 'Hong Leong Bank' },
  { code: 'BIMBMYKL', name: 'Bank Islam' },
  { code: 'ARBKMYKL', name: 'Ambank' },
  { code: 'MFBBMYKL', name: 'Alliance Bank' },
  { code: 'PHBMMYKL', name: 'Affin Bank' },
  { code: 'BKRMMYKL', name: 'Bank Kerjasama Rakyat' },
  { code: 'BSNAMYK1', name: 'Bank Simpanan Nasional' },
  { code: 'AGOBMYKL', name: 'Agrobank' },
  { code: 'RJHIMYKL', name: 'Al Rajhi Bank' },
  { code: 'UOVBMYKL', name: 'UOB Bank' },
  { code: 'HBMBMYKL', name: 'HSBC Bank' },
  { code: 'SCBLMYKX', name: 'Standard Chartered Bank' },
  { code: 'TNGDMYNB', name: 'TNG Digital (TouchNGo)' },
  { code: 'BGPYMYNB', name: 'BigPay Malaysia' },
  { code: 'BOSTMYNB', name: 'Boost' },
  { code: 'GXSPMYKL', name: 'GX Bank' },
];

const ID_TYPES = ['NRIC', 'OLD_IC', 'PASSPORT_NUMBER', 'BUSINESS_REGISTRATION_NUMBER', 'ARMY OR POLICE'];

function MyBankDetails() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [pct, setPct] = useState(0);
  const [form, setForm] = useState({
    bankCode: '',
    accountNumber: '',
    accountHolderName: '',
    idType: '',
    idValue: '',
  });

  async function loadFromServer() {
    // /vendor/clinic-profile returns { user, clinicProfile } — the payout
    // settings live under `clinicProfile.payoutSettings`.
    const resp = unwrap(await api.get('/vendor/clinic-profile'));
    const cp = resp?.clinicProfile || resp || {};
    const ps = cp?.payoutSettings || {};
    setPct(Number(cp?.clinicSharePct || 0));
    setForm({
      bankCode:          ps.bankCode || '',
      accountNumber:     ps.accountNumber || '',
      accountHolderName: ps.accountHolderName || '',
      idType:            ps.idType || '',
      idValue:           ps.idValue || '',
    });
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadFromServer();
      } catch (e) {
        if (!cancelled) setErr(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function on(k, v) { setForm((f) => ({ ...f, [k]: v })); setOkMsg(''); }

  async function save() {
    setSaving(true);
    setErr('');
    setOkMsg('');
    try {
      await payoutsApi.updateMyPayoutBank({
        bankCode:          form.bankCode.trim(),
        accountNumber:     form.accountNumber.trim(),
        accountHolderName: form.accountHolderName.trim(),
        idType:            form.idType.trim(),
        idValue:           form.idValue.trim(),
      });
      // Refetch so the form reflects what's actually stored (server may
      // trim / normalise values). Guarantees a refresh mirrors state.
      await loadFromServer();
      setOkMsg('Bank details saved. Future payouts will be sent to this account.');
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageHeader
        title="Bank details"
        subtitle="Where your clinic receives payouts from completed customer bookings."
        actions={<Link to="/my-payouts" className="btn btn-ghost"><ArrowLeft size={16} /> Back to payouts</Link>}
      />

      {err && <div className="notice warning" style={{ marginBottom: 'var(--s-4)' }}>{err}</div>}
      {okMsg && <div className="notice success" style={{ marginBottom: 'var(--s-4)' }}>{okMsg}</div>}

      <div className="notice info" style={{ marginBottom: 'var(--s-4)' }}>
        Your revenue share is <strong>{pct}%</strong> of each completed customer payment.
        This is set by MyDr.AI. Contact support if it needs to change.
      </div>

      <div className="card">
        <h3 className="card-title">Payout account</h3>
        <div className="form-group">
          <label className="form-label">Bank</label>
          <input
            className="input"
            list="clinic-bank-options"
            value={form.bankCode}
            onChange={(e) => on('bankCode', e.target.value.toUpperCase())}
            placeholder="e.g. MBBEMYKL"
          />
          <datalist id="clinic-bank-options">
            {BANK_OPTIONS.map((b) => (
              <option key={b.code} value={b.code}>{b.name}</option>
            ))}
          </datalist>
        </div>
        <div className="form-group">
          <label className="form-label">Account number</label>
          <input
            className="input"
            value={form.accountNumber}
            onChange={(e) => on('accountNumber', e.target.value.replace(/\D/g, ''))}
            placeholder="Numeric only, up to 20 digits"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Account holder name</label>
          <input
            className="input"
            value={form.accountHolderName}
            onChange={(e) => on('accountHolderName', e.target.value)}
            placeholder="As per bank record"
          />
        </div>
        <div className="grid grid-2" style={{ gap: 12 }}>
          <div className="form-group">
            <label className="form-label">ID type</label>
            <select className="input" value={form.idType} onChange={(e) => on('idType', e.target.value)}>
              <option value="">— none —</option>
              {ID_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">ID value</label>
            <input
              className="input"
              value={form.idValue}
              onChange={(e) => on('idValue', e.target.value)}
              placeholder="e.g. business reg no."
            />
          </div>
        </div>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 'var(--s-4)' }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save bank details'}
          </button>
        </div>
      </div>
    </>
  );
}

export default MyBankDetails;
